(() => {
const APPROVED_FUEL_VALUES = Object.freeze({
  natural_gas: Object.freeze(['Natural Gas']),
  propane: Object.freeze(['Propane', 'Liquid Propane'])
});
const TIGHT_FIT_BUFFER_INCHES = 1;
const INITIAL_RESULT_COUNT = 4;
const NEXT_STEP_LABELS = Object.freeze([
  'Next: choose venting',
  'Next: choose log style',
  'Next: choose fuel',
  'Next: add measurements',
  'Next: choose ignition',
  'Next: choose controls'
]);

const CATEGORY_LABELS = Object.freeze({
  manual_safety_pilot: 'Manual Controls',
  standing_pilot_controls: 'Convenience Controls',
  electronic_ignition: 'Convenience Controls'
});

// Exact, approved Shopify ignition values only. Products with a different
// value are excluded until that value is deliberately mapped here.
const CONTROL_DEFINITIONS = Object.freeze({
  'Manual Safety Pilot (Match-free Start)': Object.freeze({
    category: 'manual_safety_pilot', key: 'manual_no_remote', label: 'Manual flame control — no remote',
    description: 'Use the built-in controls to turn the burner on or off and adjust the flame.'
  }),
  'Manual Safety Pilot (Manual Flame Control)': Object.freeze({
    category: 'manual_safety_pilot', key: 'manual_no_remote', label: 'Manual flame control — no remote',
    description: 'Use the built-in control knob to operate and adjust the burner.'
  }),
  'Manual Safety Pilot (Variable Flame On/Off Knob)': Object.freeze({
    category: 'manual_safety_pilot', key: 'manual_no_remote', label: 'Manual flame control — no remote',
    description: 'Use the built-in control knob to operate and adjust the burner.'
  }),
  'Toggle Switch (Standing Pilot - Remote Ready)': Object.freeze({
    category: 'manual_safety_pilot', key: 'toggle_remote_ready', label: 'Toggle switch — remote-ready',
    description: 'Operate with the built-in toggle switch, with the option to add a compatible remote later.'
  }),
  'On/Off Remote (Standing Pilot)': Object.freeze({
    category: 'standing_pilot_controls', key: 'on_off_remote', label: 'On/off remote',
    description: 'Use a handheld remote to turn the burner on or off.'
  }),
  'Thermostat Remote (Standing Pilot)': Object.freeze({
    category: 'standing_pilot_controls', key: 'thermostat_remote', label: 'Thermostat remote',
    description: 'Use a handheld thermostat remote to cycle the burner and help maintain the selected room temperature.'
  }),
  'Variable Flame Remote (Standing Pilot)': Object.freeze({
    category: 'standing_pilot_controls', key: 'variable_flame_remote', label: 'Variable-flame remote',
    description: 'Use a handheld remote to raise or lower the flame.'
  }),
  'G18 - See-Thru Vent Free Variable Remote Burner': Object.freeze({
    category: 'standing_pilot_controls', key: 'variable_flame_remote', label: 'Variable-flame remote',
    description: 'Use the supplied remote to operate the burner and adjust flame height.'
  }),
  'Thermostat + Variable Flame Remote (Electronic Ignition)': Object.freeze({
    category: 'electronic_ignition', key: 'thermostat_variable_remote', label: 'Thermostat + variable-flame remote',
    description: 'Use the handheld remote for thermostat operation and flame-height adjustment.'
  })
});

const STEP_DEPENDENCIES = Object.freeze({
  configuration: Object.freeze(['system', 'style', 'fuel', 'ignition', 'control']),
  system: Object.freeze(['style', 'fuel', 'ignition', 'control']),
  style: Object.freeze(['fuel', 'ignition', 'control']),
  fuel: Object.freeze(['ignition', 'control']),
  ignition: Object.freeze(['control'])
});

class AgwGasLogFinder {
  constructor(root) {
    this.root = root;
    this.steps = Array.from(root.querySelectorAll('[data-agw-finder-step]'));
    this.form = root.querySelector('[data-agw-finder-form]');
    this.results = root.querySelector('[data-agw-finder-results]');
    this.resultsHeading = root.querySelector('[data-agw-finder-results-heading]');
    this.resultsIntro = root.querySelector('[data-agw-finder-results-intro]');
    this.resultsBadge = root.querySelector('.agw-finder__results-badge');
    this.resultsNotice = root.querySelector('[data-agw-finder-results-notice]');
    this.resultGrid = root.querySelector('[data-agw-finder-result-grid]');
    this.emailResults = root.querySelector('[data-agw-finder-email-results]');
    this.emailResultsForm = root.querySelector('[data-agw-finder-email-form]');
    this.emailResultsBody = root.querySelector('[data-agw-finder-email-body]');
    this.emailResultsSubmit = root.querySelector('[data-agw-finder-email-submit]');
    this.emailResultsStatus = root.querySelector('[data-agw-finder-email-status]');
    this.emailResultsEndpoint = root.dataset.emailResultsEndpoint || '';
    this.validation = root.querySelector('[data-agw-finder-validation]');
    this.actions = root.querySelector('.agw-finder__actions');
    this.backButton = root.querySelector('[data-agw-finder-back]');
    this.nextButton = root.querySelector('[data-agw-finder-next]');
    this.progressBar = root.querySelector('[data-agw-finder-progress-bar]');
    this.progressText = root.querySelector('[data-agw-finder-progress-text]');
    this.progress = root.querySelector('[data-agw-finder-progress]');
    this.stepIndicators = Array.from(root.querySelectorAll('[data-agw-finder-step-indicator]'));
    this.summaryItems = Array.from(root.querySelectorAll('[data-agw-finder-summary-item]'));
    this.consultation = root.querySelector('.agw-finder__consultation');
    this.supportPanel = root.querySelector('[data-agw-finder-support]');
    this.supportUrl = root.dataset.supportUrl || '/pages/fireplace-photo-review';
    this.resultToggleButton = root.querySelector('[data-agw-finder-results-toggle]');
    this.resultJumpButton = root.querySelector('[data-agw-finder-results-jump]');
    this.controlChoices = root.querySelector('[data-agw-finder-control-choices]');
    this.controlHint = root.querySelector('[data-agw-finder-control-hint]');
    this.systemHeading = root.querySelector('[data-agw-finder-system-heading]');
    this.systemHint = root.querySelector('[data-agw-finder-system-hint]');
    this.controlHelp = root.querySelector('[data-agw-finder-control-help]');
    this.backTopButton = root.querySelector('[data-agw-finder-back-top]');
    this.helpDisclosures = Array.from(root.querySelectorAll('.agw-finder__help'));
    this.activeHelpDisclosure = null;
    this.instanceId = root.getAttribute('aria-labelledby') || 'agw-gas-log-finder';
    this.storageKey = `agw-gas-log-finder:${window.location.pathname}${window.location.search}:${this.instanceId}`;
    this.restoringState = true;
    this.productMappings = new Map();
    this.currentStep = 0;
    this.products = this.readProducts();
    this.currentMatches = [];
    this.currentAnswers = null;
    this.resultsExpanded = false;

    if (!this.form || !this.steps.length) return;

    root.dataset.enhanced = 'true';
    this.bindEvents();
    const savedState = this.restoreState();
    this.showStep(savedState?.currentStep ?? 0, false);
    if (savedState?.view === 'results') this.renderResults({ focus: false });
    this.restoringState = false;
  }

  readProducts() {
    return Array.from(this.root.querySelectorAll('[data-agw-finder-product]')).flatMap((node) => {
      try {
        const product = JSON.parse(node.textContent);
        const mapping = this.buildProductMapping(product);
        if (!mapping) return [];
        this.productMappings.set(product.handle, mapping);
        return [product];
      } catch (error) {
        console.warn('Gas log finder skipped invalid candidate data.', error);
        return [];
      }
    });
  }

  buildProductMapping(product) {
    if (!['single_sided', 'see_through'].includes(product.configuration)
      || !['vented', 'vent_free'].includes(product.system)
      || !['oak', 'birch', 'driftwood', 'split_oak', 'charred'].includes(product.style)) return null;

    const fuels = Object.fromEntries(Object.entries(APPROVED_FUEL_VALUES).flatMap(([key, approvedValues]) => {
      const exactValue = approvedValues.find((value) => (product.fuels || []).includes(value));
      return exactValue ? [[key, exactValue]] : [];
    }));
    if (!Object.keys(fuels).length) return null;

    const categories = {};

    Object.entries(CONTROL_DEFINITIONS).forEach(([variantValue, definition]) => {
      const hasExactMatchableConfiguration = (product.variants || []).some((variant) => {
        if (!variant.available || variant.ignition !== variantValue) return false;
        const fuelKey = Object.entries(fuels).find(([, exactValue]) => exactValue === variant.fuel)?.[0];
        if (!fuelKey) return false;
        const matchingVariants = (product.variants || []).filter((candidate) => {
          return candidate.available
            && candidate.fuel === variant.fuel
            && candidate.size === variant.size
            && candidate.ignition === variantValue;
        });
        const matchingRows = (product.sizes || []).filter((row) => {
          return row.size === variant.size
            && row.ignitionTitle === variantValue
            && this.hasCompleteCompatibilityRow(row)
            && this.rowSupportsFuel(row, fuelKey);
        });
        return matchingVariants.length === 1 && matchingRows.length === 1;
      });
      if (!hasExactMatchableConfiguration) return;
      const category = categories[definition.category] || {
        label: CATEGORY_LABELS[definition.category],
        controls: []
      };
      category.controls.push({ ...definition, variantValue });
      categories[definition.category] = category;
    });

    if (!Object.keys(categories).length) return null;
    return { fuels, categories };
  }

  bindEvents() {
    this.nextButton.addEventListener('click', () => this.continue());
    this.backButton.addEventListener('click', () => this.showStep(this.currentStep - 1));
    this.form.addEventListener('change', (event) => {
      this.closeHelpPopups();
      this.clearValidation();
      const before = this.getRawAnswers();
      if (event.target.name === 'configuration') this.updateSystemChoices();
      if (['configuration', 'system'].includes(event.target.name)) {
        this.updateStyleChoices();
      }
      if (['configuration', 'system', 'style'].includes(event.target.name)) {
        this.updateFuelChoices();
      }
      if (['configuration', 'system', 'style', 'fuel'].includes(event.target.name)) {
        this.updateIgnitionChoices();
      }
      if (event.target.name === 'ignition') this.renderControlChoices(event.target.value);
      this.clearInvalidDependentAnswers(event.target.name, before);
      this.updateNextButtonState();
      this.updateSummaryRail();
      this.persistState();
    });
    this.form.addEventListener('input', () => {
      this.clearValidation();
      this.updateNextButtonState();
      this.updateSummaryRail();
      this.persistState();
    });
    this.root.querySelectorAll('[data-agw-finder-restart]').forEach((button) => {
      button.addEventListener('click', () => this.restart());
    });
    this.resultToggleButton?.addEventListener('click', () => this.toggleResults());
    this.resultJumpButton?.addEventListener('click', () => this.showAllResultsAndScroll());
    this.resultsNotice?.addEventListener('click', (event) => this.handleResultsNoticeClick(event));
    this.backTopButton?.addEventListener('click', () => this.scrollToResultsHeading());
    this.summaryItems.forEach((item) => {
      item.querySelector('[data-agw-finder-summary-button]')?.addEventListener('click', (event) => {
        const index = Number(event.currentTarget.dataset.stepIndex);
        if (Number.isInteger(index)) this.showStep(index);
      });
    });
    this.root.addEventListener('click', (event) => this.handleSupportClick(event));
    this.emailResultsForm?.addEventListener('submit', (event) => this.submitEmailResults(event));
    window.addEventListener('scroll', () => this.updateBackTopVisibility(), { passive: true });

    this.helpDisclosures.forEach((disclosure) => {
      disclosure.querySelector('summary')?.setAttribute('aria-expanded', String(disclosure.open));
    });
    this.root.addEventListener('toggle', (event) => this.handleHelpToggle(event), true);
    document.addEventListener('click', (event) => this.handleOutsideClick(event));
    document.addEventListener('keydown', (event) => this.handleHelpKeydown(event));
  }

  handleHelpToggle(event) {
    const disclosure = event.target;
    if (!(disclosure instanceof HTMLDetailsElement) || !disclosure.classList.contains('agw-finder__help')) return;

    disclosure.querySelector('summary')?.setAttribute('aria-expanded', String(disclosure.open));
    if (!disclosure.open) {
      if (this.activeHelpDisclosure === disclosure) this.activeHelpDisclosure = null;
      return;
    }

    this.closeHelpPopups({ except: disclosure });
    this.activeHelpDisclosure = disclosure;
  }

  handleOutsideClick(event) {
    if (this.activeHelpDisclosure && !this.activeHelpDisclosure.contains(event.target)) this.closeHelpPopups();
  }

  handleHelpKeydown(event) {
    if (event.key !== 'Escape' || !this.activeHelpDisclosure) return;
    event.preventDefault();
    this.closeHelpPopups({ restoreFocus: true });
  }

  closeHelpPopups({ except = null, restoreFocus = false } = {}) {
    const disclosureToRefocus = restoreFocus ? this.activeHelpDisclosure : null;
    this.helpDisclosures.forEach((disclosure) => {
      if (disclosure === except || !disclosure.open) return;
      disclosure.open = false;
      disclosure.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
    this.activeHelpDisclosure = except?.open ? except : null;
    if (disclosureToRefocus && disclosureToRefocus !== except) disclosureToRefocus.querySelector('summary')?.focus();
  }

  handleSupportClick(event) {
    const smsLink = event.target.closest('[data-agw-finder-sms]');
    if (smsLink instanceof HTMLAnchorElement) {
      this.persistState();
      event.preventDefault();
      this.openSmsLink(smsLink.href);
      return;
    }

    const chatButton = event.target.closest('[data-agw-finder-chat]');
    if (!chatButton) return;
    this.openShopifyInboxChat();
  }

  openSmsLink(href) {
    const frame = document.createElement('iframe');
    frame.hidden = true;
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.src = href;
    document.body.append(frame);
    window.setTimeout(() => frame.remove(), 1200);
  }

  openShopifyInboxChat() {
    const inboxHost = document.querySelector('shopify-chat');
    if (inboxHost instanceof HTMLElement) {
      inboxHost.setAttribute('view', 'chat');
      inboxHost.setAttribute('open', '');
      if (inboxHost.hasAttribute('open')) return;
    }

    const inboxButton = inboxHost?.shadowRoot?.querySelector('button, [role="button"]');
    if (inboxButton instanceof HTMLElement) {
      inboxButton.click();
      return;
    }

    const launcher = document.querySelector('[aria-label="Open chat"], [aria-label*="chat" i], [aria-label*="message" i], [aria-label*="ask" i]');
    if (launcher instanceof HTMLElement && !launcher.matches('[data-agw-finder-chat]')) launcher.click();
  }

  showStep(index, focus = true) {
    this.closeHelpPopups();
    delete this.root.dataset.view;
    this.currentStep = Math.max(0, Math.min(index, this.steps.length - 1));
    this.results.dataset.active = 'false';
    this.steps.forEach((step, stepIndex) => {
      step.dataset.active = String(stepIndex === this.currentStep);
      delete step.dataset.exiting;
    });
    const currentStep = this.steps[this.currentStep];
    if (currentStep.querySelector('[name="system"]')) this.updateSystemChoices();
    if (currentStep.querySelector('[name="style"]')) this.updateStyleChoices();
    if (currentStep.querySelector('[name="fuel"]')) this.updateFuelChoices();
    if (currentStep.querySelector('[name="ignition"]')) this.updateIgnitionChoices();
    if (currentStep.querySelector('[data-agw-finder-control-choices]')) {
      const selectedIgnition = this.form.querySelector('[name="ignition"]:checked');
      this.renderControlChoices(selectedIgnition?.value || null);
    }
    this.attachStepControls(currentStep);
    this.nextButton.hidden = false;
    this.progress.hidden = false;
    if (this.backTopButton) this.backTopButton.hidden = true;
    this.backButton.hidden = this.currentStep === 0;
    this.nextButton.textContent = this.currentStep === this.steps.length - 1
      ? 'See my recommendations'
      : NEXT_STEP_LABELS[this.currentStep] || 'Next';
    this.updateNextButtonState();
    this.updateProgress();
    this.updateSummaryRail();
    this.clearValidation();

    if (focus) this.focusCurrentHeading();
    this.persistState();
  }

  attachStepControls(step) {
    if (!step) return;
    if (this.validation) step.append(this.validation);
    if (this.actions) step.append(this.actions);
  }

  persistState() {
    if (this.restoringState) return;
    try {
      const answers = Object.fromEntries(new FormData(this.form).entries());
      window.sessionStorage.setItem(this.storageKey, JSON.stringify({
        answers,
        currentStep: this.currentStep,
        view: this.root.dataset.view === 'results' ? 'results' : 'steps',
        savedAt: Date.now()
      }));
    } catch (error) {
      console.warn('Gas log finder could not save progress for this browser tab.', error);
    }
  }

  restoreState() {
    try {
      const rawState = window.sessionStorage.getItem(this.storageKey);
      if (!rawState) return null;
      const state = JSON.parse(rawState);
      const isExpired = !state.savedAt || Date.now() - state.savedAt > 8 * 60 * 60 * 1000;
      if (isExpired || !state.answers || typeof state.answers !== 'object') {
        this.clearSavedState();
        return null;
      }

      Object.entries(state.answers).forEach(([name, value]) => {
        if (name === 'control') return;
        this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach((input) => {
          if (input.type === 'radio' || input.type === 'checkbox') {
            input.checked = input.value === value;
          } else {
            input.value = value;
          }
        });
      });

      const selectedIgnition = this.form.querySelector('[name="ignition"]:checked');
      this.renderControlChoices(selectedIgnition?.value || null, state.answers.control || null);
      return state;
    } catch (error) {
      this.clearSavedState();
      console.warn('Gas log finder could not restore saved progress.', error);
      return null;
    }
  }

  clearSavedState() {
    try {
      window.sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Gas log finder could not clear saved progress.', error);
    }
  }

  updateProgress() {
    const displayedStep = this.currentStep + 1;
    const percent = (displayedStep / this.steps.length) * 100;
    this.progressText.textContent = `Step ${displayedStep} of ${this.steps.length}`;
    this.progressBar.style.width = `${percent}%`;
    this.progress.setAttribute('aria-valuenow', String(displayedStep));
    this.stepIndicators.forEach((indicator, index) => {
      indicator.dataset.active = String(index === this.currentStep);
      indicator.dataset.complete = String(index < this.currentStep);
    });
  }

  updateSummaryRail() {
    if (!this.summaryItems.length) return;
    const answers = this.getAnswers();
    const values = [
      this.formatAnswer('configuration', answers.configuration),
      this.formatAnswer('system', answers.system),
      this.formatAnswer('style', answers.style),
      this.formatAnswer('fuel', answers.fuel),
      this.measurementSummary(answers),
      this.formatAnswer('ignition', answers.ignition),
      this.formatAnswer('control', answers.control)
    ];

    this.summaryItems.forEach((item, index) => {
      const value = values[index];
      item.dataset.active = String(index === this.currentStep && this.root.dataset.view !== 'results');
      item.dataset.complete = String(Boolean(value));
      item.dataset.future = String(!value && index !== this.currentStep);
      const valueNode = item.querySelector('[data-agw-finder-summary-value]');
      const button = item.querySelector('[data-agw-finder-summary-button]');
      if (button) {
        button.disabled = !value || (index === this.currentStep && this.root.dataset.view !== 'results');
        button.setAttribute('aria-label', value ? `Edit ${button.querySelector('strong')?.textContent || 'step'}: ${value}` : 'Step not answered yet');
      }
      if (!valueNode) return;
      valueNode.textContent = value || (index === this.currentStep ? 'Current step' : 'Not chosen yet');
    });
  }

  formatAnswer(name, value) {
    if (!value) return '';
    const labels = {
      configuration: {
        single_sided: 'Single-sided',
        see_through: 'See-through'
      },
      system: {
        vented: 'Vented',
        vent_free: 'Vent-free'
      },
      style: {
        no_preference: 'No preference',
        oak: 'Oak',
        birch: 'Birch',
        driftwood: 'Driftwood',
        split_oak: 'Split Oak',
        charred: 'Charred'
      },
      fuel: {
        natural_gas: 'Natural Gas',
        propane: 'Propane'
      },
      ignition: CATEGORY_LABELS,
      control: Object.fromEntries(Object.values(CONTROL_DEFINITIONS).map((control) => [control.key, control.label]))
    };
    return labels[name]?.[value] || '';
  }

  measurementSummary(answers) {
    const dimensions = [answers.frontWidth, answers.rearWidth, answers.height, answers.depth];
    if (!dimensions.every((dimension) => Number.isFinite(dimension) && dimension > 0)) return '';
    return `${answers.frontWidth}" W, ${answers.rearWidth}" rear, ${answers.height}" H, ${answers.depth}" D`;
  }

  focusCurrentHeading() {
    const heading = this.steps[this.currentStep].querySelector('[data-agw-finder-heading]');
    if (!heading) return;
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });

    const consultationRect = this.consultation.getBoundingClientRect();
    const headerOffset = 150;
    const needsScroll = consultationRect.top < headerOffset;

    if (needsScroll) {
      this.consultation.scrollIntoView({
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'nearest'
      });
    }
  }

  continue() {
    this.closeHelpPopups();
    if (!this.validateCurrentStep()) return;

    if (this.currentStep < this.steps.length - 1) {
      this.showStep(this.currentStep + 1);
      return;
    }

    this.renderResults();
  }

  validateCurrentStep() {
    const step = this.steps[this.currentStep];
    const requiredInputs = Array.from(step.querySelectorAll('[required]:not(:disabled)'));
    const radioNames = [...new Set(requiredInputs.filter((input) => input.type === 'radio').map((input) => input.name))];
    const missingRadio = radioNames.find((name) => !this.form.querySelector(`[name="${CSS.escape(name)}"]:checked`));
    const missingInput = requiredInputs.find((input) => {
      if (input.type === 'radio') return false;
      if (!input.value || !input.checkValidity()) return true;
      if (input.matches('[data-agw-finder-measurement]')) {
        const measurement = Number(input.value);
        return !Number.isFinite(measurement) || measurement <= 0;
      }
      return false;
    });

    if (!missingRadio && !missingInput) return true;

    this.validation.textContent = missingRadio
      ? 'Choose an option before continuing.'
      : 'Enter all four usable interior measurements using numbers greater than zero.';

    const target = missingInput || step.querySelector(`[name="${CSS.escape(missingRadio)}"]`);
    if (target) target.focus();
    return false;
  }

  isStepComplete(step) {
    const requiredInputs = Array.from(step.querySelectorAll('[required]:not(:disabled)'));
    const radioNames = [...new Set(requiredInputs.filter((input) => input.type === 'radio').map((input) => input.name))];
    if (radioNames.some((name) => !step.querySelector(`[name="${CSS.escape(name)}"]:checked`))) return false;
    return requiredInputs.filter((input) => input.type !== 'radio').every((input) => {
      if (!input.value || !input.checkValidity()) return false;
      if (!input.matches('[data-agw-finder-measurement]')) return true;
      const measurement = Number(input.value);
      return Number.isFinite(measurement) && measurement > 0;
    });
  }

  updateNextButtonState() {
    const currentStep = this.steps[this.currentStep];
    if (!currentStep || !this.nextButton || this.root.dataset.view === 'results') return;
    this.nextButton.disabled = !this.isStepComplete(currentStep);
  }

  clearValidation() {
    if (this.validation) this.validation.textContent = '';
  }

  getRawAnswers() {
    return Object.fromEntries(new FormData(this.form).entries());
  }

  clearInvalidDependentAnswers(changedName, before) {
    const dependentNames = STEP_DEPENDENCIES[changedName];
    if (!dependentNames) return;

    const cleared = [];
    dependentNames.forEach((name) => {
      this.form.querySelectorAll(`[name="${CSS.escape(name)}"]:checked`).forEach((input) => {
        if (!input.disabled) return;
        input.checked = false;
        cleared.push(name);
      });
    });

    const after = this.getRawAnswers();
    dependentNames.forEach((name) => {
      if (!before[name] || before[name] === after[name] || after[name]) return;
      if (!cleared.includes(name)) cleared.push(name);
    });

    if (!cleared.length) return;
    const labels = [...new Set(cleared)].map((name) => ({
      system: 'venting',
      style: 'style',
      fuel: 'fuel',
      ignition: 'control type',
      control: 'control option'
    })[name] || name);
    this.validation.textContent = `We reset ${labels.join(', ')} because it is no longer compatible with your updated answer.`;
  }

  getAnswers() {
    const data = new FormData(this.form);
    return {
      configuration: data.get('configuration'),
      system: data.get('system'),
      style: data.get('style'),
      fuel: data.get('fuel'),
      frontWidth: Number(data.get('front_width')),
      rearWidth: Number(data.get('rear_width')),
      height: Number(data.get('height')),
      depth: Number(data.get('depth')),
      ignition: data.get('ignition'),
      control: data.get('control')
    };
  }

  matchProducts(answers) {
    return this.products.flatMap((product) => {
      if (!this.productMatchesBaseAnswers(product, answers)) return [];

      const exactControl = this.getExactControl(product, answers);
      if (!exactControl) return [];

      const compatibleSizes = this.getVerifiedCompatibleSizes(product, answers, exactControl);
      if (!compatibleSizes.length) return [];

      compatibleSizes.sort((a, b) => this.numberFrom(b.row.size) - this.numberFrom(a.row.size));
      const recommended = compatibleSizes.find((size) => !size.isTightFit) || compatibleSizes[0];
      return [{
        product,
        compatibleSizes,
        recommended,
        largestCompatible: compatibleSizes[0],
        recommendationAdjusted: recommended !== compatibleSizes[0],
        exactControl
      }];
    });
  }

  getVerifiedCompatibleSizes(product, answers, exactControl) {
    const mapping = this.getProductMapping(product);
    const exactFuelValue = mapping?.fuels?.[answers.fuel];
    if (!exactFuelValue) return [];

    const variantsBySize = new Map();
    (product.variants || []).forEach((variant) => {
      if (!variant.available || variant.fuel !== exactFuelValue || variant.ignition !== exactControl.variantValue) return;
      if (!Number.isFinite(this.numberFrom(variant.size))) return;
      const variants = variantsBySize.get(variant.size) || [];
      variants.push(variant);
      variantsBySize.set(variant.size, variants);
    });

    return Array.from(variantsBySize.entries()).flatMap(([size, variants]) => {
      // A size is verifiable only when there is one unambiguous available
      // variant and one unambiguous structured requirement row for the exact
      // fuel, size, and approved ignition value.
      if (variants.length !== 1) return [];
      const rows = (product.sizes || []).filter((row) => {
        return row.size === size && row.ignitionTitle === exactControl.variantValue;
      });
      if (rows.length !== 1 || !this.rowSupportsFuel(rows[0], answers.fuel) || !this.hasCompleteCompatibilityRow(rows[0])) return [];
      return this.rowFits(rows[0], answers)
        ? [this.addFitGuidance({ row: rows[0], variant: variants[0] }, answers)]
        : [];
    });
  }

  addFitGuidance(sizeMatch, answers) {
    const requirements = {
      frontWidth: this.numberFrom(sizeMatch.row.frontWidth),
      rearWidth: this.numberFrom(sizeMatch.row.rearWidth),
      height: this.numberFrom(sizeMatch.row.height),
      depth: this.numberFrom(sizeMatch.row.depth)
    };
    const clearances = Object.fromEntries(Object.entries(requirements).map(([dimension, requirement]) => {
      return [dimension, answers[dimension] - requirement];
    }));
    const tightDimensions = Object.entries(clearances)
      .filter(([, clearance]) => clearance >= 0 && clearance <= TIGHT_FIT_BUFFER_INCHES)
      .map(([dimension, clearance]) => ({ dimension, clearance }));
    return {
      ...sizeMatch,
      clearances,
      tightDimensions,
      isTightFit: tightDimensions.length > 0
    };
  }

  supportsFuel(product, fuel) {
    const exactFuelValue = this.getProductMapping(product)?.fuels?.[fuel];
    return Boolean(exactFuelValue && (product.fuels || []).includes(exactFuelValue));
  }

  productMatchesBaseAnswers(product, answers) {
    if (product.configuration !== answers.configuration || product.system !== answers.system) return false;
    if (answers.style !== 'no_preference' && product.style !== answers.style) return false;
    return this.supportsFuel(product, answers.fuel);
  }

  getEligibleProducts({ includeSystem = true, includeStyle = true, includeFuel = true } = {}) {
    const answers = this.getAnswers();
    return this.products.filter((product) => {
      if (answers.configuration && product.configuration !== answers.configuration) return false;
      if (includeSystem && answers.system && product.system !== answers.system) return false;
      if (includeStyle && answers.style && answers.style !== 'no_preference' && product.style !== answers.style) return false;
      if (includeFuel && answers.fuel && !this.supportsFuel(product, answers.fuel)) return false;
      return Boolean(this.getProductMapping(product));
    });
  }

  updateSystemChoices() {
    const products = this.getEligibleProducts({ includeSystem: false, includeStyle: false, includeFuel: false });
    const availableSystems = new Set(products.map((product) => product.system));
    this.updateDependentChoices('[data-agw-finder-system-choice]', 'system', availableSystems);

    const configuration = this.form.querySelector('[name="configuration"]:checked')?.value;
    const onlyVentedSeeThrough = configuration === 'see_through'
      && availableSystems.size === 1
      && availableSystems.has('vented');
    if (this.systemHeading) {
      this.systemHeading.textContent = onlyVentedSeeThrough
        ? 'Vented options for your see-through or multi-sided fireplace'
        : 'Which gas log system are you considering?';
    }
    if (this.systemHint) {
      this.systemHint.textContent = onlyVentedSeeThrough
        ? 'Our current verified options for this fireplace type are vented. Vent-free options are not yet available in this finder.'
        : 'Choose the type of gas log system your fireplace is designed to use.';
    }
  }

  updateStyleChoices() {
    const products = this.getEligibleProducts({ includeStyle: false, includeFuel: false });
    const availableStyles = new Set(products.map((product) => product.style));
    this.root.querySelectorAll('[data-agw-finder-style-choice]').forEach((choice) => {
      const input = choice.querySelector('[name="style"]');
      const available = input.value === 'no_preference' ? products.length > 0 : availableStyles.has(input.value);
      choice.hidden = !available;
      input.disabled = !available;
      input.required = available;
      if (!available) input.checked = false;
      this.renderStyleMedia(choice, input.value, products);
    });
  }

  renderStyleMedia(choice, style, products) {
    const media = choice.querySelector('[data-agw-finder-style-media]');
    if (!media) return;
    media.replaceChildren();
    media.classList.remove('agw-finder__style-media--collage');

    const imageProducts = style === 'no_preference'
      ? Array.from(new Map(products.filter((product) => product.image).map((product) => [product.style, product])).values()).slice(0, 4)
      : products.filter((product) => product.style === style && product.image).slice(0, 1);

    imageProducts.forEach((product) => {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.className = 'agw-finder__style-image';
      if (style === 'no_preference') {
        image.classList.add('agw-finder__style-image--contain');
        image.dataset.imageFit = 'contain';
      }
      media.append(image);
    });
    if (imageProducts.length > 1) media.classList.add('agw-finder__style-media--collage');
    media.hidden = imageProducts.length === 0;
  }

  updateFuelChoices() {
    const products = this.getEligibleProducts({ includeFuel: false });
    const availableFuels = new Set(Object.keys(APPROVED_FUEL_VALUES).filter((fuel) => {
      return products.some((product) => this.supportsFuel(product, fuel));
    }));
    this.updateDependentChoices('[data-agw-finder-fuel-choice]', 'fuel', availableFuels);
  }

  updateDependentChoices(selector, inputName, availableValues) {
    this.root.querySelectorAll(selector).forEach((choice) => {
      const input = choice.querySelector(`[name="${inputName}"]`);
      if (!input) return;
      const available = availableValues.has(input.value);
      choice.hidden = !available;
      input.disabled = !available;
      input.required = available;
      if (!available) input.checked = false;
    });
  }

  updateIgnitionChoices() {
    const products = this.getEligibleProducts();
    const availableCategories = new Set(products.flatMap((product) => {
      return Object.keys(this.getProductMapping(product)?.categories || {});
    }));
    let selectionCleared = false;
    this.root.querySelectorAll('[data-agw-finder-ignition-choice]').forEach((choice) => {
      const input = choice.querySelector('[name="ignition"]');
      const available = availableCategories.has(input.value);
      choice.hidden = !available;
      input.disabled = !available;
      input.required = available;
      if (!available && input.checked) {
        input.checked = false;
        selectionCleared = true;
      }
    });
    if (selectionCleared) this.renderControlChoices(null);
  }

  rowSupportsFuel(row, fuel) {
    return fuel === 'natural_gas' ? Boolean(row.naturalGasData) : Boolean(row.propaneData);
  }

  rowFits(row, answers) {
    const frontWidth = this.numberFrom(row.frontWidth);
    const rearWidth = this.numberFrom(row.rearWidth);
    const height = this.numberFrom(row.height);
    const depth = this.numberFrom(row.depth);
    if (![frontWidth, rearWidth, height, depth].every((value) => Number.isFinite(value) && value > 0)) return false;
    return answers.frontWidth >= frontWidth
      && answers.rearWidth >= rearWidth
      && answers.height >= height
      && answers.depth >= depth;
  }

  hasCompleteCompatibilityRow(row) {
    return [row.frontWidth, row.rearWidth, row.height, row.depth]
      .map((value) => this.numberFrom(value))
      .every((value) => Number.isFinite(value) && value > 0);
  }

  getProductMapping(product) {
    return this.productMappings.get(product.handle) || null;
  }

  getExactControl(product, answers) {
    const category = this.getProductMapping(product)?.categories?.[answers.ignition];
    return category?.controls?.find((control) => control.key === answers.control) || null;
  }

  renderResults({ focus = true } = {}) {
    this.closeHelpPopups();
    const answers = this.getAnswers();
    const matches = this.matchProducts(answers);
    this.currentAnswers = answers;
    this.currentMatches = matches;
    this.resultsExpanded = false;
    this.resultGrid.replaceChildren();

    if (!matches.length) {
      if (this.resultsHeading) this.resultsHeading.textContent = 'We couldn’t confidently recommend a gas log set.';
      if (this.resultsIntro) {
        this.resultsIntro.hidden = false;
        this.resultsIntro.innerHTML = `
          Based on the information entered, we couldn’t verify a compatible product with complete confidence.
          <strong>That doesn’t necessarily mean one doesn’t exist.</strong>
          Some fireplaces require a manual review before we can make a recommendation.
        `;
      }
      this.hideResultsNotice();
      if (this.supportPanel) this.supportPanel.hidden = true;
      this.updateEmailResultsPayload(answers, matches);
      if (this.resultsBadge) this.resultsBadge.hidden = true;
      if (this.resultToggleButton) this.resultToggleButton.hidden = true;
      const empty = document.createElement('div');
      empty.className = 'agw-finder__empty';
      empty.innerHTML = `
        <h3 class="ff-heading">Let a Fireplace Specialist review your fireplace</h3>
        <p>Send us your fireplace photos and measurements, and we’ll help confirm the right next step.</p>
        <div class="agw-finder__empty-actions" aria-label="Support options">
          <a class="agw-finder__empty-action" href="${this.supportUrl}">Request a Complimentary Fireplace Compatibility Review</a>
          <div class="agw-finder__empty-contact-row">
            <span class="agw-finder__contact-actions agw-finder__empty-links">
              <a href="tel:+17347076589">734-707-6589</a>
              <span aria-hidden="true">|</span>
              <a href="tel:+17347076589">Call</a>
              <span aria-hidden="true">|</span>
              <a href="sms:+17347076589" data-agw-finder-sms>Text</a>
              <span aria-hidden="true">|</span>
              <button type="button" data-agw-finder-chat>Chat</button>
            </span>
          </div>
        </div>
      `;
      this.resultGrid.append(empty);
    } else {
      if (this.resultsHeading) this.resultsHeading.textContent = '🎉 You Have Compatible Matches!';
      if (this.resultsIntro) {
        this.resultsIntro.hidden = false;
        this.resultsIntro.textContent = this.resultsIntroCopy(matches.length);
      }
      if (this.supportPanel) this.supportPanel.hidden = false;
      this.updateEmailResultsPayload(answers, matches);
      if (this.resultsBadge) this.resultsBadge.hidden = false;
      this.renderResultsNotice();
      this.renderResultCards();
      this.updateResultToggle();
    }

    this.steps.forEach((step) => { step.dataset.active = 'false'; });
    this.root.dataset.view = 'results';
    this.results.dataset.active = 'true';
    this.updateSummaryRail();
    this.backButton.hidden = true;
    this.nextButton.hidden = true;
    this.root.querySelector('[data-agw-finder-progress]').hidden = true;
    this.updateBackTopVisibility();
    this.persistState();
    if (focus) {
      this.root.querySelector('[data-agw-finder-results-heading]').focus({ preventScroll: true });
      this.consultation.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  supportActionsMarkup() {
    return `
      <a class="agw-finder__empty-action" href="${this.supportUrl}">📷 Request a Complimentary Fireplace Compatibility Review</a>
      <div class="agw-finder__empty-action agw-finder__empty-action--specialist">
            <span>📞 Talk with a Fireplace Specialist</span>
            <span class="agw-finder__contact-actions agw-finder__empty-links">
              <a href="tel:+17347076589">734-707-6589</a>
              <span aria-hidden="true">|</span>
              <a href="tel:+17347076589">Call</a>
              <span aria-hidden="true">|</span>
              <a href="sms:+17347076589" data-agw-finder-sms>Text</a>
              <span aria-hidden="true">|</span>
              <button type="button" data-agw-finder-chat>Chat</button>
            </span>
          </div>
    `;
  }

  resultsIntroCopy(total) {
    if (total > INITIAL_RESULT_COUNT) {
      return `We found ${total} compatible gas log sets for your fireplace. To make choosing easier, we’ve highlighted four verified recommendations below.`;
    }
    if (total === 1) return 'We found 1 compatible gas log set for your fireplace.';
    return `We found ${total} compatible gas log sets for your fireplace.`;
  }

  renderResultCards() {
    this.resultGrid.replaceChildren();
    const visibleMatches = this.resultsExpanded ? this.currentMatches : this.currentMatches.slice(0, INITIAL_RESULT_COUNT);
    visibleMatches.forEach((match, index) => this.resultGrid.append(this.buildProductCard(match, this.currentAnswers, index)));
  }

  renderResultsNotice() {
    if (!this.resultsNotice) return;
    const hasTightFitRecommendation = this.currentMatches.some((match) => {
      const fit = match.recommendationAdjusted ? match.largestCompatible : match.recommended;
      return Boolean(fit?.isTightFit);
    });
    if (!hasTightFitRecommendation) {
      this.hideResultsNotice();
      return;
    }

    this.resultsNotice.hidden = false;
    this.resultsNotice.innerHTML = `
      <strong>A Quick Note About Your Results</strong>
      <p>One or more of your recommended gas log sets are very close to the minimum clearance based on the measurements you entered. While these appear to be compatible, we’d be happy to review your fireplace before you order so you can purchase with confidence.</p>
      <div class="agw-finder__results-notice-actions" aria-label="Fit guidance support options">
        <a href="${this.supportUrl}">📷 Request a Complimentary Fireplace Compatibility Review</a>
        <span>
          📞 Talk with a Fireplace Specialist:
          <span class="agw-finder__contact-actions">
            <a href="tel:+17347076589">734-707-6589</a>
            <span aria-hidden="true">|</span>
            <a href="tel:+17347076589">Call</a>
            <span aria-hidden="true">|</span>
            <a href="sms:+17347076589" data-agw-finder-sms>Text</a>
          </span>
        </span>
        <button type="button" data-agw-finder-chat>💬 Chat with Us</button>
      </div>
    `;
  }

  hideResultsNotice() {
    if (!this.resultsNotice) return;
    this.resultsNotice.hidden = true;
    this.resultsNotice.replaceChildren();
  }

  updateEmailResultsPayload(answers, matches) {
    if (!this.emailResults || !this.emailResultsBody) return;
    this.emailResults.hidden = !this.emailResultsEndpoint;
    if (!this.emailResultsEndpoint) return;
    this.emailResultsBody.value = this.emailResultsText(answers, matches);
    if (this.emailResultsStatus) {
      this.emailResultsStatus.textContent = '';
      this.emailResultsStatus.classList.remove('agw-finder__email-status--error');
    }
  }

  emailResultsText(answers, matches) {
    const selected = [
      ['Fireplace type', this.labelFor('configuration', answers.configuration)],
      ['System', this.labelFor('system', answers.system)],
      ['Style', this.labelFor('style', answers.style)],
      ['Fuel', this.labelFor('fuel', answers.fuel)],
      ['Measurements', this.measurementSummary(answers)],
      ['Ignition category', this.labelFor('ignition', answers.ignition)],
      ['Control preference', this.labelFor('control', answers.control)]
    ];
    const recommendationLines = matches.length
      ? matches.map((match, index) => {
        const { product, recommended } = match;
        const title = `${index + 1}. ${product.title}`;
        const size = `Recommended size: ${recommended?.row?.size || 'Confirm on product page'}`;
        const price = `Price shown: ${recommended?.variant?.price || product.price || 'See product page'}`;
        const link = `Link: ${this.absoluteUrl(recommended?.variant?.url || product.url || '')}`;
        return [title, size, price, link].join('\n');
      }).join('\n\n')
      : 'No compatible products were shown with complete confidence. Manual fireplace review recommended.';

    return [
      'American Gas Works Gas Log Product Finder Results',
      '',
      'Customer selections:',
      ...selected.map(([label, value]) => `${label}: ${value || 'Not selected'}`),
      '',
      'Compatible recommendations:',
      recommendationLines,
      '',
      'Important fit note:',
      'Confirm final compatibility, clearances, and installation requirements on the product page or with an American Gas Works fireplace specialist before ordering.'
    ].join('\n');
  }

  emailResultsTemplateData(answers, matches) {
    return {
      fireplace_type: this.labelFor('configuration', answers.configuration),
      system: this.labelFor('system', answers.system),
      style: this.labelFor('style', answers.style),
      fuel: this.labelFor('fuel', answers.fuel),
      front_width: `${answers.frontWidth} in`,
      rear_width: `${answers.rearWidth} in`,
      height: `${answers.height} in`,
      depth: `${answers.depth} in`,
      control_preference: this.labelFor('control', answers.control),
      recommendations_count: matches.length,
      recommendations_text: this.emailRecommendationsText(matches),
      recommendations_html: this.emailRecommendationsHtml(matches),
      compatibility_review_url: this.absoluteUrl(this.supportUrl),
      ...this.emailRecommendationFields(matches)
    };
  }

  emailResultsHtml(answers, matches) {
    const details = [
      ['Fireplace type', this.labelFor('configuration', answers.configuration)],
      ['System', this.labelFor('system', answers.system)],
      ['Style preference', this.labelFor('style', answers.style)],
      ['Fuel', this.labelFor('fuel', answers.fuel)],
      ['Front width', `${answers.frontWidth} in`],
      ['Rear width', `${answers.rearWidth} in`],
      ['Height', `${answers.height} in`],
      ['Depth', `${answers.depth} in`],
      ['Control preference', this.labelFor('control', answers.control)]
    ].map(([label, value]) => `
      <tr>
        <td style="padding: 10px 0; border-bottom: 1px solid #e6e1dc; font-weight: 700;">${this.escapeHtml(label)}</td>
        <td style="padding: 10px 0; border-bottom: 1px solid #e6e1dc;">${this.escapeHtml(value || 'Not selected')}</td>
      </tr>
    `).join('');

    return `
      <div style="margin: 0; padding: 0; background: #f7f3ef; color: #29211f; font-family: Arial, sans-serif;">
        <div style="max-width: 680px; margin: 0 auto; padding: 28px 18px;">
          <div style="background: #fff; border: 1px solid #e6e1dc; padding: 28px;">
            <p style="margin: 0 0 8px; color: #8c0617; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;">American Gas Works</p>
            <h1 style="margin: 0 0 18px; font-family: Georgia, serif; font-size: 30px; line-height: 1.15;">Your Gas Log Finder Results</h1>
            <p style="margin: 0 0 14px;">Thanks for using our Gas Log Product Finder. Here is a copy of your fireplace details and compatible product matches so you can come back to them later.</p>
            <p style="margin: 0 0 24px;">These results are a helpful starting point. Before ordering, always confirm final fit, clearances, and installation requirements on the product page or with an American Gas Works fireplace specialist.</p>
            <h2 style="margin: 0 0 12px; color: #8c0617; font-family: Georgia, serif; font-size: 22px;">Your fireplace details</h2>
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin: 0 0 26px;">${details}</table>
            <h2 style="margin: 0 0 12px; color: #8c0617; font-family: Georgia, serif; font-size: 22px;">Your compatible product matches</h2>
            ${this.emailRecommendationsHtml(matches)}
            <div style="margin: 24px 0; padding: 16px; border-left: 4px solid #008080; background: #eff7f7;">
              <p style="margin: 0;"><strong>Want us to double-check it?</strong> If your measurements are close, your fireplace has unusual clearances, or you just want a second set of eyes before ordering, our team can review your fireplace photos and measurements.</p>
            </div>
            <p style="margin: 0 0 22px;"><a href="${this.escapeAttribute(this.absoluteUrl(this.supportUrl))}" style="display: inline-block; padding: 12px 18px; background: #8c0617; color: #fff; text-decoration: none; font-weight: 700;">Request a Complimentary Fireplace Compatibility Review</a></p>
            <p style="margin: 0 0 6px;"><strong>American Gas Works</strong></p>
            <p style="margin: 0 0 6px;"><a href="tel:+17347076589" style="color: #008080;">Call 734-707-6589</a> | <a href="sms:+17347076589" style="color: #008080;">Text us</a></p>
            <p style="margin: 0;"><a href="https://americangasworks.com" style="color: #008080;">americangasworks.com</a></p>
          </div>
        </div>
      </div>
    `;
  }

  emailRecommendationsHtml(matches) {
    if (!matches.length) {
      return '<p>No compatible products were shown with complete confidence. A manual fireplace review is recommended.</p>';
    }

    const items = matches.map((match, index) => {
      const { product, recommended } = match;
      const title = this.escapeHtml(product.title);
      const size = this.escapeHtml(recommended?.row?.size || 'Confirm on product page');
      const price = this.escapeHtml(recommended?.variant?.price || product.price || 'See product page');
      const url = this.absoluteUrl(recommended?.variant?.url || product.url || '');
      const image = product.image ? this.absoluteUrl(product.image) : '';
      const imageAlt = this.escapeAttribute(product.imageAlt || product.title);
      const imageHtml = image
        ? `<td style='width:120px;padding:0 16px 0 0;vertical-align:top;'><a href='${this.escapeAttribute(url)}'><img src='${this.escapeAttribute(image)}' alt='${imageAlt}' width='120' style='display:block;width:120px;max-width:120px;height:auto;border:1px solid #e6e1dc;'></a></td>`
        : '';
      return `<table role='presentation' width='100%' cellspacing='0' cellpadding='0' style='margin:0 0 18px;border-bottom:1px solid #e6e1dc;padding-bottom:18px;'><tr>${imageHtml}<td style='vertical-align:top;'><p style='margin:0 0 6px;font-weight:700;'>${index + 1}. ${title}</p><p style='margin:0 0 4px;'>Recommended size: ${size}</p><p style='margin:0 0 10px;'>Price shown: ${price}</p><a href='${this.escapeAttribute(url)}' style='color:#008080;font-weight:700;'>View product match</a></td></tr></table>`;
    }).join('');

    return `<div style='margin:0;padding:0;background:#ffffff;color:#29211f;font-family:Arial,sans-serif;'><h2 style='margin:0 0 12px;color:#8c0617;font-family:Georgia,serif;font-size:22px;'>Your compatible product matches</h2>${items}<p style='margin:20px 0 0;'><a href='${this.escapeAttribute(this.absoluteUrl(this.supportUrl))}' style='display:inline-block;padding:12px 18px;background:#8c0617;color:#ffffff;text-decoration:none;font-weight:700;'>Request a Complimentary Fireplace Compatibility Review</a></p></div>`;
  }

  emailRecommendationsText(matches) {
    if (!matches.length) return 'No compatible products were shown with complete confidence. Manual fireplace review recommended.';
    return matches.slice(0, INITIAL_RESULT_COUNT).map((match, index) => {
      const { product, recommended } = match;
      return [
        `${index + 1}. ${product.title}`,
        `Recommended size: ${recommended?.row?.size || 'Confirm on product page'}`,
        `Price shown: ${recommended?.variant?.price || product.price || 'See product page'}`,
        `Link: ${this.absoluteUrl(recommended?.variant?.url || product.url || '')}`
      ].join('\n');
    }).join('\n\n');
  }

  emailRecommendationFields(matches) {
    return matches.slice(0, INITIAL_RESULT_COUNT).reduce((fields, match, index) => {
      const fieldIndex = index + 1;
      const { product, recommended } = match;
      fields[`recommendation_${fieldIndex}_title`] = product.title;
      fields[`recommendation_${fieldIndex}_size`] = recommended?.row?.size || 'Confirm on product page';
      fields[`recommendation_${fieldIndex}_price`] = recommended?.variant?.price || product.price || 'See product page';
      fields[`recommendation_${fieldIndex}_url`] = this.absoluteUrl(recommended?.variant?.url || product.url || '');
      fields[`recommendation_${fieldIndex}_image`] = product.image ? this.absoluteUrl(product.image) : '';
      return fields;
    }, {});
  }

  async submitEmailResults(event) {
    event.preventDefault();
    if (!this.emailResultsForm || !this.emailResultsStatus) return;

    const formData = new FormData(this.emailResultsForm);
    const email = String(formData.get('email') || '').trim();
    const marketingOptIn = formData.get('marketing_opt_in') === 'yes';
    if (!email || !this.emailResultsForm.reportValidity()) return;

    if (!this.emailResultsEndpoint) {
      this.setEmailStatus('Email delivery is almost ready. Please call or text us and we can send these results manually for now.', true);
      return;
    }

    const answers = this.currentAnswers || this.getAnswers();
    const matches = this.currentMatches || [];
    const behaviorTag = 'used-product-finder';
    const marketingSourceTag = 'gas-log-finder-opt-in';
    const welcomeStartedTag = 'welcome-series-started';
    const welcomeCompletedTag = 'welcome-series-completed';
    const payload = {
      source: 'Gas Log Product Finder',
      template_alias: 'gas-log-finder-results',
      email,
      marketing_opt_in: marketingOptIn,
      marketing_consent_text: marketingOptIn
        ? 'Yes, send me helpful fireplace tips, gas log buying guides, and occasional offers from American Gas Works.'
        : '',
      marketing_consent_source: 'Gas Log Finder results email form',
      marketing_consent_timestamp: marketingOptIn ? new Date().toISOString() : '',
      behavior_tag: behaviorTag,
      marketing_source_tag: marketingOptIn ? marketingSourceTag : '',
      customer_tags: marketingOptIn ? [behaviorTag, marketingSourceTag].join(', ') : behaviorTag,
      welcome_started_tag: welcomeStartedTag,
      welcome_completed_tag: welcomeCompletedTag,
      submitted_at: new Date().toISOString(),
      page_url: window.location.href,
      answers: {
        configuration: answers.configuration,
        system: answers.system,
        style: answers.style,
        fuel: answers.fuel,
        front_width: answers.frontWidth,
        rear_width: answers.rearWidth,
        height: answers.height,
        depth: answers.depth,
        ignition: answers.ignition,
        control: answers.control
      },
      template_data: this.emailResultsTemplateData(answers, matches),
      result_text: this.emailResultsText(answers, matches),
      html_body: this.emailResultsHtml(answers, matches),
      subject: 'Your American Gas Works Gas Log Finder Results'
    };

    this.setEmailSubmitting(true);
    try {
      this.submitWebhookForm(payload);
      this.setEmailStatus('Your results are on their way. Check your inbox in a minute or two.', false);
      this.emailResultsForm.reset();
      this.emailResultsBody.value = payload.result_text;
    } catch (error) {
      console.warn('Gas log finder could not email results.', error);
      this.setEmailStatus('We could not send the email just now. Your results are still shown here, and you can call or text us for help.', true);
    } finally {
      this.setEmailSubmitting(false);
    }
  }

  submitWebhookForm(payload) {
    const templateData = payload.template_data || {};
    const fields = {
      email: payload.email,
      source: payload.source,
      template_alias: payload.template_alias,
      subject: payload.subject,
      marketing_opt_in: payload.marketing_opt_in ? 'true' : 'false',
      marketing_consent_text: payload.marketing_consent_text,
      marketing_consent_source: payload.marketing_consent_source,
      marketing_consent_timestamp: payload.marketing_consent_timestamp,
      behavior_tag: payload.behavior_tag,
      marketing_source_tag: payload.marketing_source_tag,
      customer_tags: payload.customer_tags,
      welcome_started_tag: payload.welcome_started_tag,
      welcome_completed_tag: payload.welcome_completed_tag,
      submitted_at: payload.submitted_at,
      page_url: payload.page_url,
      fireplace_type: templateData.fireplace_type,
      system: templateData.system,
      style: templateData.style,
      fuel: templateData.fuel,
      front_width: templateData.front_width,
      rear_width: templateData.rear_width,
      height: templateData.height,
      depth: templateData.depth,
      control_preference: templateData.control_preference,
      recommendations_count: templateData.recommendations_count,
      product_finder_results: templateData.recommendations_text,
      recommendations_text: templateData.recommendations_text,
      recommendations_html: templateData.recommendations_html,
      html_body: payload.html_body,
      recommendation_1_title: templateData.recommendation_1_title,
      recommendation_1_size: templateData.recommendation_1_size,
      recommendation_1_price: templateData.recommendation_1_price,
      recommendation_1_url: templateData.recommendation_1_url,
      recommendation_1_image: templateData.recommendation_1_image,
      recommendation_2_title: templateData.recommendation_2_title,
      recommendation_2_size: templateData.recommendation_2_size,
      recommendation_2_price: templateData.recommendation_2_price,
      recommendation_2_url: templateData.recommendation_2_url,
      recommendation_2_image: templateData.recommendation_2_image,
      recommendation_3_title: templateData.recommendation_3_title,
      recommendation_3_size: templateData.recommendation_3_size,
      recommendation_3_price: templateData.recommendation_3_price,
      recommendation_3_url: templateData.recommendation_3_url,
      recommendation_3_image: templateData.recommendation_3_image,
      recommendation_4_title: templateData.recommendation_4_title,
      recommendation_4_size: templateData.recommendation_4_size,
      recommendation_4_price: templateData.recommendation_4_price,
      recommendation_4_url: templateData.recommendation_4_url,
      recommendation_4_image: templateData.recommendation_4_image
    };

    const form = document.createElement('form');
    const frame = document.createElement('iframe');
    const targetName = `agw-email-results-${Date.now()}`;

    form.hidden = true;
    form.method = 'POST';
    form.action = this.emailResultsEndpoint;
    form.target = targetName;
    form.acceptCharset = 'UTF-8';

    Object.entries(fields).forEach(([name, value]) => {
      if (value == null || value === '') return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(value);
      form.append(input);
    });

    frame.hidden = true;
    frame.setAttribute('aria-hidden', 'true');
    frame.name = targetName;
    frame.tabIndex = -1;
    document.body.append(form, frame);
    form.submit();
    window.setTimeout(() => {
      form.remove();
      frame.remove();
    }, 10000);
  }

  setEmailSubmitting(isSubmitting) {
    if (!this.emailResultsSubmit) return;
    this.emailResultsSubmit.disabled = isSubmitting;
    this.emailResultsSubmit.textContent = isSubmitting ? 'Sending...' : 'Email My Results';
  }

  setEmailStatus(message, isError) {
    if (!this.emailResultsStatus) return;
    this.emailResultsStatus.textContent = message;
    this.emailResultsStatus.classList.toggle('agw-finder__email-status--error', Boolean(isError));
  }

  absoluteUrl(url) {
    try {
      return new URL(url, window.location.origin).href;
    } catch (error) {
      return '';
    }
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value || '');
    return div.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }

  labelFor(name, value) {
    if (!value) return '';
    const input = this.form?.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
    const label = input?.closest('.agw-finder__choice')?.querySelector('.agw-finder__choice-title');
    if (label?.textContent?.trim()) return label.textContent.trim();
    return String(value)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  handleResultsNoticeClick(event) {
    this.handleSupportClick(event);
  }

  updateResultToggle() {
    if (!this.resultToggleButton) return;
    const total = this.currentMatches.length;
    this.resultToggleButton.hidden = total <= INITIAL_RESULT_COUNT;
    this.resultToggleButton.textContent = this.resultsExpanded
      ? 'Show Fewer Matches'
      : `View All ${total} Compatible Gas Logs`;
    this.resultToggleButton.setAttribute('aria-expanded', String(this.resultsExpanded));
  }

  toggleResults() {
    if (!this.currentMatches.length) return;
    const scrollY = window.scrollY;
    const wasExpanded = this.resultsExpanded;
    this.resultsExpanded = !this.resultsExpanded;
    this.renderResultCards();
    this.updateResultToggle();
    if (wasExpanded) {
      window.requestAnimationFrame(() => this.scrollToResultsHeading());
      return;
    }
    this.resultToggleButton?.focus({ preventScroll: true });
    window.scrollTo({ top: scrollY, behavior: 'auto' });
  }

  showAllResultsAndScroll() {
    if (!this.currentMatches.length) return;
    if (!this.resultsExpanded && this.currentMatches.length > INITIAL_RESULT_COUNT) {
      this.resultsExpanded = true;
      this.renderResultCards();
      this.updateResultToggle();
    }
    this.resultGrid?.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  scrollToResultsHeading() {
    this.resultsHeading?.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    this.resultsHeading?.focus({ preventScroll: true });
  }

  updateBackTopVisibility() {
    if (!this.backTopButton) return;
    const inResults = this.root.dataset.view === 'results';
    const headingTop = this.resultsHeading?.getBoundingClientRect().top || 0;
    this.backTopButton.hidden = !inResults || headingTop > -220;
  }

  buildProductCard(match, answers, index = 0) {
    const { product, recommended, compatibleSizes } = match;
    const { variant } = recommended;
    const article = document.createElement('article');
    article.className = 'agw-finder__product';
    if (index === 0) article.classList.add('agw-finder__product--hero');

    const media = document.createElement('div');
    media.className = 'agw-finder__product-image-wrap';
    if (product.image) {
      const image = document.createElement('img');
      image.className = 'agw-finder__product-image';
      image.src = product.image;
      image.alt = product.imageAlt || product.title;
      image.loading = 'lazy';
      media.append(image);
    }

    const content = document.createElement('div');
    content.className = 'agw-finder__product-content';

    const matchLabel = document.createElement('p');
    matchLabel.className = 'agw-finder__match-label';
    matchLabel.textContent = index === 0 ? 'Recommended for Your Fireplace' : 'Compatible match';

    const title = document.createElement('h3');
    title.className = 'agw-finder__product-title';
    title.textContent = product.title;

    const price = document.createElement('div');
    price.className = 'agw-finder__product-price';
    const priceLabel = document.createElement('span');
    priceLabel.textContent = 'Recommended Configuration Price';
    const priceValue = document.createElement('strong');
    priceValue.textContent = variant?.price || product.price;
    price.append(priceLabel, priceValue);

    const sizeSummary = this.buildSizeSummary(match);
    const tightFitAdvisory = this.buildTightFitAdvisory(match);

    const reasons = document.createElement('ul');
    reasons.className = 'agw-finder__match-list';
    this.matchReasons(match, answers).forEach((reason) => {
      const item = document.createElement('li');
      item.textContent = reason;
      reasons.append(item);
    });

    const reasonsHeading = document.createElement('h4');
    reasonsHeading.className = 'agw-finder__reasons-heading ff-heading';
    reasonsHeading.textContent = 'Why this fits your fireplace';

    const confirmation = document.createElement('p');
    confirmation.className = 'agw-finder__confirmation-note';
    confirmation.textContent = index === 0
      ? 'This recommendation is based on your fireplace type, venting, fuel, measurements, style, and verified product data.'
      : 'Before ordering, confirm final fit and model-specific operation in the product owner’s manual.';

    const link = document.createElement('a');
    link.className = 'agw-finder__button agw-finder__button--primary';
    link.href = variant?.url || product.url;
    link.textContent = 'Shop Product';

    content.append(matchLabel, title, sizeSummary);
    if (tightFitAdvisory) content.append(tightFitAdvisory);
    content.append(price, reasonsHeading, reasons, confirmation, link);
    article.append(media, content);
    return article;
  }

  buildSizeSummary(match) {
    const { compatibleSizes, recommended } = match;
    const recommendedSize = this.numberFrom(recommended.row.size);
    const tightFitAlternatives = compatibleSizes.filter((size) => (
      size !== recommended
      && size.isTightFit
      && this.numberFrom(size.row.size) > recommendedSize
    ));
    const summary = document.createElement('div');
    summary.className = 'agw-finder__size-summary';

    const recommendedLabel = document.createElement('span');
    recommendedLabel.className = 'agw-finder__size-label';
    recommendedLabel.textContent = 'Recommended Size';
    const recommendedValue = document.createElement('strong');
    recommendedValue.className = 'agw-finder__recommended-size';
    recommendedValue.textContent = recommended.row.size;
    summary.append(recommendedLabel, recommendedValue);

    if (tightFitAlternatives.length) {
      const alsoLabel = document.createElement('span');
      alsoLabel.className = 'agw-finder__size-label agw-finder__size-label--also';
      alsoLabel.textContent = 'Tight-fit option';
      const alsoList = document.createElement('ul');
      alsoList.className = 'agw-finder__also-compatible';
      tightFitAlternatives.forEach(({ row }) => {
        const item = document.createElement('li');
        item.textContent = row.size;
        alsoList.append(item);
      });
      summary.append(alsoLabel, alsoList);
    }
    return summary;
  }

  buildTightFitAdvisory(match) {
    const fit = match.recommendationAdjusted ? match.largestCompatible : match.recommended;
    if (!fit?.isTightFit) return null;

    const advisory = document.createElement('div');
    advisory.className = 'agw-finder__tight-fit';
    const heading = document.createElement('strong');
    heading.textContent = 'American Gas Works fit guidance';
    const copy = document.createElement('p');
    copy.textContent = match.recommendationAdjusted
      ? `Your measurements are very close to the minimum clearance for the ${fit.row.size} log set. While it should fit based on the information provided, we recommend the ${match.recommended.row.size} set for more installation flexibility. We’d still be happy to review your fireplace before you order.`
      : `Your measurements are very close to the minimum clearance for the ${fit.row.size} log set. While it should fit based on the information provided, we’d like to review your fireplace before recommending this size with confidence.`;
    advisory.append(heading, copy);
    return advisory;
  }

  matchReasons(match, answers) {
    const { product, exactControl } = match;
    const configuration = answers.configuration === 'see_through' ? 'See-through' : 'Single-sided';
    const system = answers.system === 'vented' ? 'vented' : 'vent-free';
    const fuel = answers.fuel === 'natural_gas' ? 'natural gas' : 'propane';
    const mapping = product && this.getProductMapping(product);
    const category = mapping?.categories?.[answers.ignition];
    const reasons = [
      `Designed for a ${configuration.toLowerCase()}, ${system} fireplace`,
      'Fits the fireplace dimensions you entered',
      `Supports ${fuel}`,
      `Works with ${category?.label || 'the selected ignition'} and ${exactControl?.label || 'control'}`,
      `Matches your ${this.styleLabel(product.style).toLowerCase()} log style`
    ];
    if (match.recommendationAdjusted) {
      reasons.push('Provides more installation flexibility than the larger tight-fit option');
    } else if (match.recommended.isTightFit) {
      reasons.push('Qualifies from the measurements provided; a professional review is recommended');
    } else {
      reasons.push('Provides more than 1″ of room beyond every minimum clearance');
    }
    return reasons;
  }

  renderControlChoices(categoryKey, selectedControlKey = this.form.querySelector('[name="control"]:checked')?.value || null) {
    if (!this.controlChoices) return;
    this.controlChoices.replaceChildren();

    const category = this.getSharedCategory(categoryKey);
    if (!category) {
      if (this.controlHint) this.controlHint.textContent = 'Choose an ignition category first.';
      if (this.controlHelp) this.controlHelp.innerHTML = '<p>Choose a control category first.</p>';
      return;
    }

    if (this.controlHint) this.controlHint.textContent = category.controlHint;
    this.renderControlHelp(category.controls);

    category.controls.forEach((control) => {
      const choice = document.createElement('div');
      choice.className = 'agw-finder__choice';
      choice.dataset.agwFinderControlKey = control.key;

      const input = document.createElement('input');
      input.id = `${this.instanceId}-${control.key}`;
      input.type = 'radio';
      input.name = 'control';
      input.value = control.key;
      input.required = true;
      input.checked = control.key === selectedControlKey;

      const label = document.createElement('label');
      label.htmlFor = input.id;

      const visual = document.createElement('span');
      visual.className = `agw-finder__choice-visual agw-finder__choice-visual--control agw-finder__choice-visual--${control.key}`;
      visual.setAttribute('aria-hidden', 'true');
      visual.innerHTML = this.controlVisual(control.key);

      const title = document.createElement('span');
      title.className = 'agw-finder__choice-title';
      title.textContent = control.label;

      const copy = document.createElement('span');
      copy.className = 'agw-finder__choice-copy';
      copy.textContent = control.description;

      label.append(visual, title, copy);
      choice.append(input, label);
      this.controlChoices.append(choice);
    });
  }

  renderControlHelp(controls) {
    if (!this.controlHelp) return;
    const descriptions = controls.map((control) => {
      if (control.key === 'toggle_remote_ready') return '<p><strong>Toggle switch — remote-ready</strong> operates using the built-in toggle switch, with the option to add a compatible remote later.</p>';
      if (control.key === 'on_off_remote') return '<p>An <strong>on/off remote</strong> turns the burner on or off from a handheld remote.</p>';
      if (control.key === 'thermostat_remote') return '<p>A <strong>thermostat remote</strong> automatically cycles the burner to help maintain the selected room temperature.</p>';
      if (control.key === 'variable_flame_remote' || control.key === 'thermostat_variable_remote') return '<p>A <strong>variable-flame remote</strong> lets you raise or lower the flame.</p>';
      if (control.key === 'manual_no_remote') return '<p><strong>Manual flame control — no remote</strong> operates directly at the fireplace.</p>';
      return '';
    }).filter(Boolean);
    this.controlHelp.innerHTML = `${[...new Set(descriptions)].join('')}<p class="agw-finder__help-note">Features vary by product. Always refer to the product owner's manual for model-specific operation details.</p>`;
  }

  controlVisual(controlKey) {
    if (controlKey === 'manual_no_remote') {
      return '<svg viewBox="0 0 160 96" focusable="false" aria-hidden="true"><circle cx="80" cy="52" r="30" /><circle cx="80" cy="52" r="22" /><circle cx="80" cy="52" r="7" /><path class="agw-finder__choice-flow" d="M80 30v13M57 62l12-6M103 62l-12-6" /><circle cx="80" cy="23" r="3" /><circle cx="52" cy="67" r="3" /><circle cx="108" cy="67" r="3" /><text class="agw-finder__control-label" x="68" y="17">PILOT</text><text class="agw-finder__control-label" x="31" y="84">OFF</text><text class="agw-finder__control-label" x="112" y="84">ON</text></svg>';
    }
    if (controlKey === 'toggle_remote_ready') {
      return '<svg viewBox="0 0 160 96" focusable="false" aria-hidden="true"><rect x="48" y="16" width="64" height="70" rx="10" /><rect x="63" y="30" width="34" height="42" rx="6" /><path class="agw-finder__choice-flow" d="M68 64 92 38" /><circle cx="80" cy="24" r="3" /><circle cx="80" cy="78" r="3" /><path class="agw-finder__choice-flow" d="M57 86h46" /></svg>';
    }
    if (controlKey === 'on_off_remote') {
      return '<svg viewBox="0 0 160 96" focusable="false" aria-hidden="true"><rect x="57" y="14" width="46" height="68" rx="9" /><circle cx="80" cy="42" r="16" /><path class="agw-finder__choice-flow" d="M80 30v14m-9-5a13 13 0 1 0 18 0M70 64h20" /></svg>';
    }
    if (controlKey === 'thermostat_remote') {
      return '<svg viewBox="0 0 160 96" focusable="false" aria-hidden="true"><rect x="57" y="14" width="46" height="68" rx="9" /><text class="agw-finder__control-label agw-finder__control-label--large" x="69" y="40">72°</text><path class="agw-finder__choice-flow" d="M76 56v13" /><circle cx="76" cy="72" r="5" /><path class="agw-finder__choice-flow" d="M88 56h10M88 65h8" /></svg>';
    }
    if (controlKey === 'variable_flame_remote') {
      return '<svg viewBox="0 0 160 96" focusable="false" aria-hidden="true"><rect x="57" y="14" width="46" height="68" rx="9" /><path d="M80 51c-8-6-6-16 1-25 2 7 9 11 9 18 0 5-4 8-10 11Z" /><path class="agw-finder__choice-flow" d="M116 26v43m-8-34 8-9 8 9m-16 25 8 9 8-9M70 65h20" /></svg>';
    }
    if (controlKey === 'thermostat_variable_remote') {
      return '<svg viewBox="0 0 160 96" focusable="false" aria-hidden="true"><rect x="52" y="13" width="56" height="70" rx="10" /><text class="agw-finder__control-label agw-finder__control-label--large" x="64" y="36">72°</text><path d="M80 62c-7-5-5-14 1-22 2 6 8 10 8 15 0 5-4 8-9 10Z" /><path class="agw-finder__choice-flow" d="M119 29v38m-7-30 7-8 7 8m-14 22 7 8 7-8" /></svg>';
    }
    return '<svg viewBox="0 0 160 96" focusable="false" aria-hidden="true"><path d="M54 72h52M80 72V42" /><path d="M80 42c-8-7-6-17 1-27 2 8 9 13 10 21 1 7-4 12-11 14Z" /><path class="agw-finder__choice-flow" d="M42 70l32-20" /></svg>';
  }

  getSharedCategory(categoryKey) {
    const categories = this.getEligibleProducts()
      .map((product) => this.getProductMapping(product)?.categories?.[categoryKey])
      .filter(Boolean);
    if (!categories.length) return null;

    const controls = new Map();
    categories.forEach((category) => {
      category.controls.forEach((control) => {
        if (!controls.has(control.key)) controls.set(control.key, control);
      });
    });
    return {
      label: categories[0].label,
      controlHint: controls.size === 1
        ? 'One verified control choice is available for this combination.'
        : 'Choose from the verified controls available for this combination.',
      controls: Array.from(controls.values())
    };
  }

  restart() {
    this.closeHelpPopups();
    this.clearSavedState();
    this.form.reset();
    this.renderControlChoices(null);
    this.resultGrid.replaceChildren();
    if (this.resultsHeading) this.resultsHeading.textContent = 'Your compatible options';
    if (this.resultsIntro) this.resultsIntro.hidden = false;
    if (this.supportPanel) this.supportPanel.hidden = false;
    this.nextButton.hidden = false;
    this.root.querySelector('[data-agw-finder-progress]').hidden = false;
    if (this.backTopButton) this.backTopButton.hidden = true;
    this.showStep(0);
  }

  numberFrom(value) {
    if (typeof value === 'number') return value;
    const match = String(value || '').match(/[\d.]+/);
    return match ? Number(match[0]) : Number.NaN;
  }

  capitalize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  styleLabel(style) {
    return {
      oak: 'Oak',
      birch: 'Birch',
      driftwood: 'Driftwood',
      split_oak: 'Split Oak',
      charred: 'Charred'
    }[style] || 'Selected';
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

document.querySelectorAll('[data-agw-gas-log-finder]').forEach((root) => new AgwGasLogFinder(root));
})();
